// src/services/pulseCache.js
//
// In-memory cache for Pulse pane API responses.
// Mirrors the pattern in locationCache.js (5-min TTL, module singleton).
//
// Kept for compatibility with retired analytical panes; do not restore them.
// Values include calculation explanations and FX provenance together.
// Previously used to serve instant values for Cost/seat and
// Avg util on repeat navigations — the same data the Cost Trajectory and
// Utilization Over Time panes display, cached from the previous visit.
//
// Cache keys (exported constants):
//   costTrajKey(includeServiced)  -> cost-trajectory response object
//   utilTrendKey(includeServiced) -> utilization-trend response object
//
// Invalidation: any time showServiced toggles, the old key is simply not
// found (different key), so stale filtered/unfiltered data never bleeds in.

const TTL_MS = 5 * 60 * 1000; // 5 minutes — same as locationCache

/** @type {Map<string, { value: any, expiresAt: number }>} */
const store = new Map();

/** Get a cache entry. Returns undefined on miss or expired entry. */
export function get(key) {
  const entry = store.get(key);
  if (!entry) return undefined;
  if (Date.now() >= entry.expiresAt) {
    store.delete(key);
    return undefined;
  }
  return entry.value;
}

/** Set a cache entry with a fixed 5-minute TTL. */
export function set(key, value) {
  store.set(key, { value, expiresAt: Date.now() + TTL_MS });
}

/** Drop every entry. */
export function invalidateAll() {
  store.clear();
}

/** Test-only: reset state. */
export function __resetForTests() {
  store.clear();
}

/** Test-only: peek at the underlying map size. */
export function __sizeForTests() {
  return store.size;
}

/** Cache key for the cost-trajectory endpoint result. */
export const costTrajKey = (includeServiced) =>
  includeServiced ? "cost-traj:serviced" : "cost-traj:no-serviced";

/** Cache key for the utilization-trend endpoint result. */
export const utilTrendKey = (includeServiced) =>
  includeServiced ? "util-trend:serviced" : "util-trend:no-serviced";

export const TTL_MS_FOR_TESTS = TTL_MS;
